import { Net } from "./net.js";
import { SnapshotBuffer } from "./snapshots.js";
import { bindInput } from "./input.js";
import { computeCamera, render, screenToWorld, type Camera } from "./render.js";
import { $, show, hide, setHudMass, setMergeTimer, renderLeaderboard, renderBuffs } from "./ui.js";

const WORKER_URL = (import.meta as ImportMeta & { env: { VITE_WORKER_URL?: string } }).env.VITE_WORKER_URL
  ?? "http://localhost:8787";

const canvas = $("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d")!;
function resize() {
  canvas.width = window.innerWidth * devicePixelRatio;
  canvas.height = window.innerHeight * devicePixelRatio;
  canvas.style.width = window.innerWidth + "px";
  canvas.style.height = window.innerHeight + "px";
  ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
}
resize();
window.addEventListener("resize", resize);

let net: Net | null = null;
let buf = new SnapshotBuffer();
let myPid = -1;
let world: { w: number; h: number; portals: { id: number; x: number; y: number }[] } = { w: 6000, h: 6000, portals: [] };
let cam: Camera = { x: 3000, y: 3000, zoom: 1.5 };
let input = bindInput(canvas);
let lastInputSentAt = 0;
let playing = false;
// User-controlled zoom multiplier (< 1 zooms in, > 1 zooms out).
// Applied on top of the auto-zoom that scales with mass.
let userZoom = 1;
// Wheel zoom range. The out side is sized so a fully-zoomed-out
// client sees roughly the entire server interest area (their line of
// sight) — at MAX_AUTO_ZOOM=1.0 that's 1500 * 1.5 = 2250 u radius,
// ≈ visible diagonal at userZoom=2.2 on a typical 1080p display.
const USER_ZOOM_MIN = 0.25;
const USER_ZOOM_MAX = 2.25;

function startGame(nick: string) {
  if (net) net.close();
  buf = new SnapshotBuffer();
  net = new Net();
  // Reset any leftover wheel-zoom so it doesn't persist across deaths.
  userZoom = 1;
  const wsUrl = WORKER_URL.replace(/^http/, "ws") + "/ws";
  net.connect(wsUrl, nick, {
    onWelcome: (pid, w, portals) => { myPid = pid; world = { ...w, portals }; playing = true; hide("overlay"); show("hud"); show("leaderboard"); },
    onState: (s) => buf.push({ tick: s.tick, recvAt: performance.now(), cells: s.cells, food: s.food, leaderboard: s.leaderboard }),
    onDead: (killedBy) => {
      playing = false;
      hide("hud"); hide("leaderboard");
      ($("killed-by") as HTMLSpanElement).textContent = killedBy;
      show("overlay"); hide("join-panel"); show("dead-panel"); hide("full-panel");
    },
    onFull: () => {
      playing = false;
      show("overlay"); hide("join-panel"); hide("dead-panel"); show("full-panel");
    },
    onClose: (info) => {
      // If we're still in-game, the server dropped us without a dead/full
      // message. Treat clean closes (code 1000 — server-initiated, e.g.
      // restart) the same as unclean ones for UX: drop back to join.
      if (playing) {
        playing = false;
        hide("hud"); hide("leaderboard");
        show("overlay"); show("join-panel"); hide("dead-panel"); hide("full-panel");
        console.warn(`disconnected (code=${info.code} reason=${info.reason || "n/a"} clean=${info.wasClean})`);
      }
    },
  });
}

($("play") as HTMLButtonElement).addEventListener("click", () => {
  const nick = ($("nick") as HTMLInputElement).value.trim();
  startGame(nick);
});
($("respawn") as HTMLButtonElement).addEventListener("click", () => {
  // Keep the dead-panel visible until the new connection's welcome arrives.
  // onWelcome hides the entire overlay; no need to flash the join panel.
  const nick = ($("nick") as HTMLInputElement).value.trim();
  startGame(nick);
});
($("retry") as HTMLButtonElement).addEventListener("click", () => {
  hide("full-panel"); show("join-panel");
});

// Enter on the overlay clicks the primary action of whichever panel is
// visible: Play on the join panel, Respawn on dead, Retry on full.
window.addEventListener("keydown", (e) => {
  if (e.key !== "Enter") return;
  if ($("overlay").classList.contains("hidden")) return;
  if (!$("join-panel").classList.contains("hidden")) {
    e.preventDefault();
    ($("play") as HTMLButtonElement).click();
  } else if (!$("dead-panel").classList.contains("hidden")) {
    e.preventDefault();
    ($("respawn") as HTMLButtonElement).click();
  } else if (!$("full-panel").classList.contains("hidden")) {
    e.preventDefault();
    ($("retry") as HTMLButtonElement).click();
  }
});

function loop() {
  requestAnimationFrame(loop);
  const now = performance.now();
  const view = buf.interpolate(now);

  // Consume any pending wheel input — applied even on the join screen so
  // a stray scroll doesn't get stockpiled and dumped on Play.
  if (input.wheelDelta !== 0) {
    userZoom *= Math.pow(1.0015, input.wheelDelta);
    userZoom = Math.max(USER_ZOOM_MIN, Math.min(USER_ZOOM_MAX, userZoom));
    input.wheelDelta = 0;
  }

  if (playing) {
    cam = computeCamera(view, myPid, cam);
    const renderCam: Camera = { ...cam, zoom: cam.zoom * userZoom };

    // Send input ~30 Hz. Use renderCam so screen→world matches what's drawn.
    if (net && now - lastInputSentAt > 33) {
      if (input.keyX !== 0 || input.keyY !== 0) {
        const len = Math.hypot(input.keyX, input.keyY);
        const tx = cam.x + (input.keyX / len) * 500;
        const ty = cam.y + (input.keyY / len) * 500;
        net.sendInput(tx, ty);
      } else {
        const worldPos = screenToWorld(canvas, input.mouseX, input.mouseY, renderCam);
        net.sendInput(worldPos.x, worldPos.y);
      }
      lastInputSentAt = now;
    }
    if (input.splitPressed) {
      input.splitPressed = false;
      net?.sendSplit();
    }

    // HUD.
    const own = view.cells.filter(c => c.pid === myPid);
    setHudMass(own.reduce((s, c) => s + c.m, 0));
    renderLeaderboard(view.leaderboard);
    // Merge timer: only meaningful when split into multiple cells AND
    // at least one still has a non-zero cooldown. Show the longest one.
    if (own.length > 1) {
      const maxMg = own.reduce((m, c) => Math.max(m, c.mg ?? 0), 0);
      setMergeTimer(maxMg > 0 ? maxMg : null);
    } else {
      setMergeTimer(null);
    }
    // Buff timers: take max remaining ms per kind across all owned cells.
    const buffMap: Record<string, number> = {};
    for (const c of own) {
      if (c.pw) buffMap["pw"] = Math.max(buffMap["pw"] ?? 0, c.pw);
      if (c.om) buffMap["om"] = Math.max(buffMap["om"] ?? 0, c.om);
      if (c.sp) buffMap["sp"] = Math.max(buffMap["sp"] ?? 0, c.sp);
      if (c.vc) buffMap["vc"] = Math.max(buffMap["vc"] ?? 0, c.vc);
    }
    renderBuffs(
      (["pw", "om", "sp", "vc"] as const)
        .filter(k => buffMap[k] !== undefined)
        .map(k => ({ kind: k, remainingMs: buffMap[k]! }))
    );

    render(ctx, view, renderCam, myPid, world.w, world.h, world.portals);
  } else {
    render(ctx, view, { ...cam, zoom: cam.zoom * userZoom }, myPid, world.w, world.h, world.portals);
  }
}
loop();
